/**
 * Home Hero Product (Redesign) — gallery thumbs + main slide, purchase options, cart.
 * Scoped per section root; same cart/variant logic as home-hero-product.js.
 */

class HomeHeroProductRedesign {
  constructor(root) {
    this.container = root;
    if (!this.container) return;

    this.imageTrack = null;
    this.mainView = this.container.querySelector('[data-hero-main-view]');
    this.mainSlides = [...this.container.querySelectorAll('[data-hero-slide]')];
    this.imageDots = [...this.container.querySelectorAll('[data-image-dot]')];
    this.purchaseOptions = this.container.querySelectorAll('.purchase-option__button');
    this.primaryCTA = this.container.querySelector('#primary-cta');
    this.productForm = this.container.querySelector('form[data-home-hero-product-form]');
    this.stickyAtbBar = this.container.querySelector('[data-hero-sticky-atb]');
    this.stickyAtbBtn = this.container.querySelector('[data-hero-sticky-atb-btn]');
    this.stickyAtbMq =
      typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 749px)') : null;
    this._stickyAtbRaf = null;

    this.currentImageIndex = 0;
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.selectedVariantId = null;
    this.selectedSellingPlanId = '';
    this.selectedBundleVariantId = null;
    this.selectedDiscountCode = '';
    this.selectedPurchaseType = '';

    this.thumbHoverMedia = window.matchMedia('(min-width: 1024px) and (hover: hover)');
    this.desktopStickMq = window.matchMedia('(min-width: 1024px)');
    this.mediaCol = this.container.querySelector('.hero-product-redesign__media');
    this.stickyWrap = this.container.querySelector('[data-hero-sticky-wrap]');
    this.container._heroRedesignInstance = this;

    this.init();
  }

  init() {
    console.log('[HomeHeroProductRedesign] Initializing...', {
      hasContainer: !!this.container,
      hasForm: !!this.productForm,
      hasCTA: !!this.primaryCTA,
      purchaseOptionCount: this.purchaseOptions.length
    });

    this.setupImageStack();
    this.setupPurchaseOptions();
    this.setupFormSubmitInterceptor();
    this.setupPrimaryCTA();
    this.setupAccessibility();
    this.setupResizeObserver();
    this.setupStickyTopTracking();
    this.setupStickyAddToBasketBar();
    requestAnimationFrame(() => this.syncFixedGallery());

    // Fetch variant IDs for options where product picker didn't resolve (page template)
    this.fetchVariantIdsForEmptyOptions().then(() => {
      this.autoSelectFirstOption();
    });
  }

  async fetchVariantIdsForEmptyOptions() {
    if (!this.purchaseOptions.length) return;
    const toFetch = [];
    const toFetchGift = [];
    this.purchaseOptions.forEach((btn) => {
      const variantId = (btn.dataset.variantId || '').trim();
      const handle = (btn.dataset.productHandle || '').trim();
      if (!variantId && handle) toFetch.push({ btn, handle });
      const purchaseType = btn.dataset.purchaseType || '';
      const bundleVariantId = (btn.dataset.bundleVariantId || '').trim();
      const giftHandle = (btn.dataset.giftProductHandle || '').trim();
      const isMultiItem = ['subscription_plus_gift', 'one_time_plus_gift', 'bundle'].includes(purchaseType);
      
      if (isMultiItem && !bundleVariantId && giftHandle) {
        toFetchGift.push({ btn, handle: giftHandle });
      }
    });
    await Promise.all([
      ...toFetch.map(({ btn, handle }) =>
        fetch(`/products/${handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .then((product) => {
            if (product?.variants?.[0]?.id) {
              btn.dataset.variantId = String(product.variants[0].id);
            }
          })
          .catch(() => { })
      ),
      ...toFetchGift.map(({ btn, handle }) =>
        fetch(`/products/${handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .then((product) => {
            if (product?.variants?.[0]?.id) {
              btn.dataset.bundleVariantId = String(product.variants[0].id);
            }
          })
          .catch(() => { })
      )
    ]);
  }

  autoSelectFirstOption() {
    if (this.purchaseOptions.length > 0) {
      // Log all purchase option state for debugging
      console.log('[HomeHeroProductRedesign] Purchase options found:', this.purchaseOptions.length);
      this.purchaseOptions.forEach((btn, i) => {
        console.log(`[HomeHeroProductRedesign] Option ${i}:`, {
          label: btn.querySelector('.purchase-option__label')?.textContent?.trim(),
          variantId: btn.dataset.variantId,
          sellingPlanId: btn.dataset.sellingPlanId,
          purchaseType: btn.dataset.purchaseType,
          productHandle: btn.dataset.productHandle,
          isPrimary: btn.classList.contains('primary')
        });
      });

      // Find the first primary option or default to the first one
      let firstOption = this.purchaseOptions[0];

      for (let option of this.purchaseOptions) {
        if (option.classList.contains('primary') || option.dataset.isPrimary === 'true') {
          firstOption = option;
          break;
        }
      }

      // Simulate click on the first option
      setTimeout(() => {
        console.log('[HomeHeroProductRedesign] Auto-selecting option:', firstOption.querySelector('.purchase-option__label')?.textContent?.trim());
        firstOption.click();
      }, 100);
    } else {
      console.warn('[HomeHeroProductRedesign] No purchase options found in DOM');
    }
  }

  setupImageStack() {
    this.mainSlides = [...this.container.querySelectorAll('[data-hero-slide]')];
    this.imageDots = [...this.container.querySelectorAll('[data-image-dot]')];
    this.mainView = this.container.querySelector('[data-hero-main-view]');
    this.currentImageIndex = 0;

    if (!this.mainSlides.length) return;

    if (this.imageDots.length) {
      this.updateActiveDot(0);
      this.imageDots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToImage(index));
        dot.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.goToImage(index);
          }
        });
        dot.addEventListener('mouseenter', () => {
          if (this.thumbHoverMedia.matches) {
            this.goToImage(index);
          }
        });
        dot.addEventListener('focus', () => {
          this.goToImage(index);
        });
      });
    } else {
      this.mainSlides.forEach((s, i) => s.classList.toggle('is-active', i === 0));
    }

    this.setupMainSwipe();
  }

  setupMainSwipe() {
    const el = this.mainView;
    if (!el || this.mainSlides.length < 2) return;

    let startX = null;
    el.addEventListener('touchstart', (e) => {
      startX = e.changedTouches[0].screenX;
    }, { passive: true });

    el.addEventListener('touchend', (e) => {
      if (startX == null) return;
      const endX = e.changedTouches[0].screenX;
      const dx = endX - startX;
      startX = null;
      if (Math.abs(dx) < 50) return;
      if (dx < 0) {
        this.goToImage(Math.min(this.currentImageIndex + 1, this.mainSlides.length - 1));
      } else {
        this.goToImage(Math.max(this.currentImageIndex - 1, 0));
      }
    }, { passive: true });
  }

  setupPurchaseOptions() {
    if (!this.purchaseOptions.length) return;

    this.purchaseOptions.forEach(option => {
      option.addEventListener('click', this.handlePurchaseOptionClick.bind(this));

      // Handle keyboard navigation
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handlePurchaseOptionClick(e);
        }
      });
    });
  }

  setupFormSubmitInterceptor() {
    if (!this.productForm) return;
    this.productForm.addEventListener('submit', (e) => {
      const idInput = this.productForm.querySelector('input[name="id"]');
      const variantId = idInput ? idInput.value : '';

      if (!variantId || variantId.trim() === '') {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.showErrorMessage('Please select a purchase option first. If you’ve already done so, configure the Product for each option in the theme editor (Home Hero Product Redesign → Purchase options).');
        return;
      }

      if (this.selectedPurchaseType === 'bundle' && this.selectedVariantId && this.selectedBundleVariantId) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.setAddToCartLoading(true);
        (async () => {
          await this.cartAdd(this.selectedVariantId, 1, '');
          const response = await this.cartAdd(this.selectedBundleVariantId, 1, '');
          const parsedState = await this.ensureCartSections(response);
          this.openCartDrawer(parsedState);
        })()
          .catch(err => this.showErrorMessage(err.message || 'Could not add bundle to basket.'))
          .finally(() => this.setAddToCartLoading(false));
      }
      if ((this.selectedPurchaseType === 'subscription_plus_gift' || this.selectedPurchaseType === 'one_time_plus_gift') && this.selectedVariantId && this.selectedBundleVariantId) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.setAddToCartLoading(true);
        (async () => {
          const sellingPlanId = this.selectedPurchaseType === 'subscription_plus_gift' ? (this.selectedSellingPlanId || '').trim() : '';
          await this.cartAdd(this.selectedVariantId, 1, sellingPlanId);
          const response = await this.cartAdd(this.selectedBundleVariantId, 1, '');
          if ((this.selectedPurchaseType === 'subscription_plus_gift' || this.selectedPurchaseType === 'one_time_plus_gift') && window.celfLinkedTracker) window.celfLinkedTracker.push(this.selectedVariantId);
          const parsedState = await this.ensureCartSections(response);
          this.openCartDrawer(parsedState);
        })()
          .catch(err => this.showErrorMessage(err.message || 'Could not add to basket. Please try again.'))
          .finally(() => this.setAddToCartLoading(false));
      }
    }, true);
  }

  setAddToCartLoading(loading) {
    if (!this.primaryCTA) return;
    if (loading) {
      this.primaryCTA.setAttribute('aria-disabled', 'true');
      this.primaryCTA.disabled = true;
      this.primaryCTA.classList.add('is-loading');
    } else {
      this.primaryCTA.removeAttribute('aria-disabled');
      this.primaryCTA.disabled = false;
      this.primaryCTA.classList.remove('is-loading');
    }
    this.syncStickyATB();
  }

  setupPrimaryCTA() {
    if (!this.primaryCTA) return;

    // When we have a product form, Add to Basket is the submit button – form handles one_time/subscription
    if (this.productForm) {
      this.disablePrimaryCTA();
      return;
    }

    // Standalone (page) mode: primary CTA is a button that triggers JS add-to-cart
    this.disablePrimaryCTA();
    this.primaryCTA.addEventListener('click', (e) => {
      e.preventDefault();
      this.addToCart();
    });
    this.primaryCTA.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.addToCart();
      }
    });
    this.primaryCTA.setAttribute('title', 'Please select a purchase option first');
  }

  setupAccessibility() {
    // Ensure proper ARIA labels and roles
    if (this.imageDots.length > 1) {
      this.imageDots.forEach((dot, index) => {
        dot.setAttribute('aria-label', `Go to image ${index + 1} of ${this.imageDots.length}`);
        dot.setAttribute('role', 'button');
        dot.setAttribute('tabindex', '0');
      });
    }

    // Set up accordion accessibility
    const accordionItems = this.container.querySelectorAll('.accordion-item');
    accordionItems.forEach(item => {
      const trigger = item.querySelector('.accordion-trigger');
      const content = item.querySelector('.accordion-content');

      if (trigger && content) {
        trigger.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-hidden', 'true');

        item.addEventListener('toggle', () => {
          const isOpen = item.hasAttribute('open');
          trigger.setAttribute('aria-expanded', isOpen.toString());
          content.setAttribute('aria-hidden', (!isOpen).toString());
        });
      }
    });
  }

  setupResizeObserver() {
    if (!window.ResizeObserver || !this.container) return;
    const onResize = () => {
      this.updateStickyOffset();
      this.syncFixedGallery();
    };
    new ResizeObserver(onResize).observe(this.container);
    if (this.stickyWrap) {
      new ResizeObserver(onResize).observe(this.stickyWrap);
    }
  }

  /**
   * Keep gallery `top` aligned below sticky header (and announcement bar) as they show/hide on scroll.
   * Global listeners once — --sticky-offset is shared on <html>.
   */
  setupStickyAddToBasketBar() {
    if (!this.stickyAtbBar || !this.stickyAtbBtn || !this.primaryCTA || !this.stickyAtbMq) return;

    const updateStickyVisibility = () => {
      if (!this.stickyAtbMq.matches) {
        this.stickyAtbBar.hidden = true;
        this.stickyAtbBar.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('hero-product-redesign-sticky-atb-visible');
        return;
      }
      const rect = this.primaryCTA.getBoundingClientRect();
      const scrolledPastPrimary = rect.bottom < 0;
      this.stickyAtbBar.hidden = !scrolledPastPrimary;
      this.stickyAtbBar.setAttribute('aria-hidden', scrolledPastPrimary ? 'false' : 'true');
      document.body.classList.toggle('hero-product-redesign-sticky-atb-visible', scrolledPastPrimary);
    };

    const schedule = () => {
      if (this._stickyAtbRaf != null) return;
      this._stickyAtbRaf = requestAnimationFrame(() => {
        this._stickyAtbRaf = null;
        updateStickyVisibility();
      });
    };

    this.stickyAtbBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.primaryCTA && !this.primaryCTA.disabled) {
        this.primaryCTA.click();
      }
    });

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    this.stickyAtbMq.addEventListener('change', schedule);

    schedule();
    this.syncStickyATB();

    if (this.primaryCTA && typeof MutationObserver !== 'undefined') {
      this._primaryCtaObserver = new MutationObserver(() => {
        this.syncStickyATB();
      });
      this._primaryCtaObserver.observe(this.primaryCTA, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled']
      });
    }
  }

  syncStickyATB() {
    if (!this.stickyAtbBtn || !this.primaryCTA) return;
    const defaultText = this.primaryCTA.getAttribute('data-default-cta-text') || 'Add to Basket';
    const src = this.primaryCTA.querySelector('.celf-atb__text');
    const dst = this.stickyAtbBtn.querySelector('.celf-atb__text');
    if (src && dst) {
      const pt = (src.textContent || '').trim();
      if (pt.includes('—') || (pt.includes(' - ') && pt.length > defaultText.length + 2)) {
        dst.textContent = defaultText;
      } else {
        dst.textContent = pt || defaultText;
      }
    }

    const stickyPriceEl = this.container.querySelector('[data-hero-sticky-atb-price]');
    if (stickyPriceEl) {
      const leadEl = this.container.querySelector('[data-hero-lead-price]');
      const fallbackEl = this.container.querySelector('.product-pricing .price-current');
      const fromLead = leadEl && (leadEl.textContent || '').trim() ? (leadEl.textContent || '').trim() : '';
      const fromFallback = fallbackEl ? (fallbackEl.textContent || '').trim() : '';
      const priceText = fromLead || fromFallback;
      if (priceText) stickyPriceEl.textContent = priceText;
    }

    this.stickyAtbBtn.disabled = this.primaryCTA.disabled;
    this.stickyAtbBtn.classList.toggle('disabled', this.primaryCTA.classList.contains('disabled'));
    const ad = this.primaryCTA.getAttribute('aria-disabled');
    if (ad !== null) {
      this.stickyAtbBtn.setAttribute('aria-disabled', ad);
    } else {
      this.stickyAtbBtn.removeAttribute('aria-disabled');
    }
    ['is-loading', 'is-success', 'is-error', 'loading'].forEach((c) => {
      this.stickyAtbBtn.classList.toggle(c, this.primaryCTA.classList.contains(c));
    });
  }

  setupStickyTopTracking() {
    this.updateStickyOffset();

    if (HomeHeroProductRedesign._stickyTopListenersBound) return;
    HomeHeroProductRedesign._stickyTopListenersBound = true;

    let scrollTicking = false;
    const schedule = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        HomeHeroProductRedesign.updateAllStickyOffsets();
        document.querySelectorAll('.home-hero-product-redesign').forEach((root) => {
          const inst = root._heroRedesignInstance;
          if (inst && typeof inst.syncFixedGallery === 'function') {
            inst.syncFixedGallery();
          }
        });
        scrollTicking = false;
      });
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    if (window.ResizeObserver) {
      const headerSection = document.getElementById('shopify-section-header');
      if (headerSection) {
        new ResizeObserver(schedule).observe(headerSection);
      }
      const ann = document.querySelector('#shopify-section-announcement-bar');
      if (ann) {
        new ResizeObserver(schedule).observe(ann);
      }
    }
  }

  goToImage(index) {
    if (index < 0 || index >= this.mainSlides.length) return;
    this.currentImageIndex = index;
    this.updateActiveDot(index);
    requestAnimationFrame(() => this.syncFixedGallery());
  }

  updateActiveDot(index) {
    this.imageDots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('active');
        dot.removeAttribute('aria-current');
      }
    });
    this.mainSlides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
  }

  handlePurchaseOptionClick(event) {
    const button = event.currentTarget;
    const purchaseType = (button.dataset.purchaseType || 'one_time').trim();
    const variantId = (button.dataset.variantId || '').trim();
    const sellingPlanId = (button.dataset.sellingPlanId || '').trim();
    const bundleVariantId = (button.dataset.bundleVariantId || '').trim();
    const discountCode = (button.dataset.discountCode || '').trim();

    this.selectedVariantId = variantId;
    this.selectedSellingPlanId = sellingPlanId;
    this.selectedBundleVariantId = bundleVariantId;
    this.selectedDiscountCode = discountCode;
    this.selectedPurchaseType = purchaseType;

    this.updateLeadPricing(button);
    this.updatePrimaryCTA(button);
    this.updateFormForSelectedOption();
    this.updatePurchaseOptionStates(button);

    const canAdd = variantId && (
      purchaseType === 'one_time' ||
      (purchaseType === 'subscription' && sellingPlanId) ||
      (purchaseType === 'bundle' && bundleVariantId) ||
      (purchaseType === 'subscription_plus_gift' && bundleVariantId) ||
      (purchaseType === 'one_time_plus_gift' && bundleVariantId)
    );

    console.log('[HomeHeroProductRedesign] Option clicked:', {
      purchaseType, variantId, sellingPlanId, bundleVariantId, discountCode, canAdd,
      hasForm: !!this.productForm, hasCTA: !!this.primaryCTA
    });

    if (canAdd) this.enablePrimaryCTA();
    else this.disablePrimaryCTA();
  }

  updateFormForSelectedOption() {
    if (!this.productForm || !this.selectedVariantId) return;
    const idInput = this.productForm.querySelector('input[name="id"]');
    const bundleIdInput = this.productForm.querySelector('[data-bundle-variant-id-input]');
    const sellingPlanInput = this.productForm.querySelector('[data-selling-plan-input]');
    if (idInput) idInput.value = this.selectedVariantId;
    if (bundleIdInput) bundleIdInput.value = this.selectedBundleVariantId || '';
    if (sellingPlanInput) sellingPlanInput.value = (this.selectedPurchaseType === 'subscription' || this.selectedPurchaseType === 'subscription_plus_gift') ? this.selectedSellingPlanId : '';
  }

  updateLeadPricing(button) {
    const wrap = this.container.querySelector('[data-hero-lead-pricing]');
    if (!wrap || !button) return;
    const priceEl = wrap.querySelector('[data-hero-lead-price]');
    const compareEl = wrap.querySelector('[data-hero-lead-compare]');
    const saveEl = wrap.querySelector('[data-hero-lead-save]');
    const priceCurrent = button.querySelector('.purchase-option__price-current');
    const priceCompare = button.querySelector('.purchase-option__price-compare');
    if (priceEl && priceCurrent) {
      priceEl.textContent = (priceCurrent.textContent || '').trim();
    }
    if (compareEl) {
      const ct = priceCompare && priceCompare.textContent ? priceCompare.textContent.trim() : '';
      if (ct) {
        compareEl.textContent = ct;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }
    if (saveEl) {
      const saveBadge = (button.dataset.savingsBadge || '').trim();
      if (saveBadge) {
        saveEl.textContent = saveBadge;
        saveEl.hidden = false;
      } else {
        saveEl.textContent = '';
        saveEl.hidden = true;
      }
    }
  }

  updatePrimaryCTA(selectedButton) {
    if (!this.primaryCTA) return;

    const textEl = this.primaryCTA.querySelector('.celf-atb__text');
    const defaultText = this.primaryCTA.getAttribute('data-default-cta-text') || 'Add to Basket';
    if (textEl) {
      if (selectedButton && this.purchaseOptions.length) {
        const priceText = selectedButton.querySelector('.purchase-option__price-current')?.textContent?.trim() || '';
        textEl.textContent = priceText ? `${defaultText} — ${priceText}` : defaultText;
      } else {
        textEl.textContent = defaultText;
      }
    }
    this.primaryCTA.setAttribute('aria-describedby', 'selected-option');

    // Reset any transient states
    this.primaryCTA.classList.remove('is-loading', 'is-success', 'is-error');

    this.primaryCTA.dataset.selectedPurchaseType = this.selectedPurchaseType;
    this.primaryCTA.dataset.selectedVariantId = this.selectedVariantId;
    this.primaryCTA.dataset.selectedSellingPlanId = this.selectedSellingPlanId;
    this.primaryCTA.dataset.selectedBundleVariantId = this.selectedBundleVariantId || '';
    this.primaryCTA.dataset.selectedDiscountCode = this.selectedDiscountCode || '';
    this.syncStickyATB();
  }

  enablePrimaryCTA() {
    if (this.primaryCTA) {
      this.primaryCTA.disabled = false;
      this.primaryCTA.classList.remove('disabled');
      this.primaryCTA.setAttribute('aria-disabled', 'false');
      this.primaryCTA.removeAttribute('title');
    }
    this.syncStickyATB();
  }

  disablePrimaryCTA() {
    if (this.primaryCTA) {
      this.primaryCTA.disabled = true;
      this.primaryCTA.classList.add('disabled');
      this.primaryCTA.setAttribute('aria-disabled', 'true');
      this.primaryCTA.setAttribute('title', 'Please select a purchase option first');
    }
    this.syncStickyATB();
  }

  updatePurchaseOptionStates(selectedButton) {
    this.purchaseOptions.forEach(button => {
      button.classList.remove('primary');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-checked', 'false');
    });

    selectedButton.classList.add('primary');
    selectedButton.setAttribute('aria-pressed', 'true');
    selectedButton.setAttribute('aria-checked', 'true');
  }

  // ---------------------------------------------------------------------------
  // Cart integration helpers
  // ---------------------------------------------------------------------------

  /** Locate the theme's cart-drawer or cart-notification element (cached). */
  getCartElement() {
    if (this._cartEl === undefined) {
      this._cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification') || null;
    }
    return this._cartEl;
  }

  /** Section IDs the cart-drawer needs for a live update. */
  getCartSectionIds() {
    const cart = this.getCartElement();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map((s) => s.id);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  /**
   * Core fetch wrapper that talks to /cart/add.js the same way the theme's
   * product-form.js does, including requesting updated section HTML so the
   * cart-drawer can refresh itself.
   */
  async cartAdd(variantId, quantity = 1, sellingPlanId = '') {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    if (sellingPlanId) formData.append('selling_plan', sellingPlanId);

    // Ask Shopify to include rendered cart sections in the response
    const sectionIds = this.getCartSectionIds();
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);

    const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    console.log('[HomeHeroProductRedesign] cartAdd →', { variantId, quantity, sellingPlanId, cartAddUrl });

    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    });

    const body = await res.json().catch(() => null);
    console.log('[HomeHeroProductRedesign] cartAdd response', res.status, body);

    if (!res.ok || (body && body.status)) {
      const msg = (body && (body.description || body.message)) || `HTTP ${res.status}`;
      throw new Error(msg);
    }

    return body;
  }

  /**
   * Redirect to checkout with a discount code applied (e.g. for subscription+ gift flow).
   */
  redirectToCheckoutWithDiscount(discountCode) {
    const checkoutUrl = (window.routes && window.routes.checkout_url) || '/checkout';
    const url = discountCode ? `${checkoutUrl}?discount=${encodeURIComponent(discountCode)}` : checkoutUrl;
    window.location.href = url;
  }

  /**
   * After a successful cartAdd, hand the response to the cart-drawer so it
   * re-renders itself and slides open.  If the theme has no drawer, redirect
   * to the cart page.
   */
  openCartDrawer(response) {
    const cart = this.getCartElement();
    if (cart && typeof cart.renderContents === 'function') {
      console.log('[HomeHeroProductRedesign] Opening cart drawer');
      cart.renderContents(response);
      return;
    }
    // No cart drawer — redirect to cart page
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    console.log('[HomeHeroProductRedesign] No cart drawer, redirecting to', cartUrl);
    window.location = cartUrl;
  }

  /**
   * Fetch fresh cart sections via Section Rendering API. Use this when the
   * cart/add response may have null or stale sections (e.g. when adding the
   * first item or doing sequential adds like subscription_plus_gift).
   */
  async fetchCartSections() {
    const sectionIds = this.getCartSectionIds();
    const base = (typeof window.Shopify !== 'undefined' && window.Shopify.routes?.root) || window.location.pathname || '/';
    const url = `${base}?sections=${sectionIds.join(',')}`;
    const res = await fetch(url);
    const sections = await res.json().catch(() => ({}));
    return sections;
  }

  /**
   * Ensure we have valid cart sections for the drawer. When adding the first
   * item or doing sequential adds, cart/add may return null or stale sections.
   * Fetch fresh sections via Section Rendering API using current page URL.
   */
  async ensureCartSections(response) {
    const sections = response?.sections || {};
    const cartDrawerHtml = sections['cart-drawer'];
    const hasValidDrawer = cartDrawerHtml && typeof cartDrawerHtml === 'string' && cartDrawerHtml.length > 100;

    let finalSections = sections;
    if (!hasValidDrawer) {
      const freshSections = await this.fetchCartSections();
      finalSections = { ...sections, ...freshSections };
    }

    return {
      id: response?.id || response?.items?.[0]?.id || response?.key,
      item_count: response?.item_count ?? (response?.items?.length > 0 ? 1 : 0),
      sections: finalSections
    };
  }

  // ---------------------------------------------------------------------------
  // Add-to-cart entry point (standalone / no product form)
  // ---------------------------------------------------------------------------

  async addToCart() {
    if (!this.primaryCTA || this.primaryCTA.disabled) return;

    const variantId = this.primaryCTA.dataset.selectedVariantId || this.selectedVariantId;
    const purchaseType = this.primaryCTA.dataset.selectedPurchaseType || this.selectedPurchaseType;

    if (!variantId) {
      this.showErrorMessage('Please select a purchase option first.');
      return;
    }

    console.log('[HomeHeroProductRedesign] addToCart', { variantId, purchaseType });

    // Enter loading state
    this.primaryCTA.disabled = true;
    this.primaryCTA.classList.remove('is-success', 'is-error');
    this.primaryCTA.classList.add('is-loading');
    this.syncStickyATB();

    try {
      let response;

      if (purchaseType === 'bundle') {
        const bundleVariantId = this.primaryCTA.dataset.selectedBundleVariantId || this.selectedBundleVariantId;
        if (!bundleVariantId) throw new Error('Bundle product is required for this option.');
        await this.cartAdd(variantId, 1, '');
        response = await this.cartAdd(bundleVariantId, 1, '');
        this.primaryCTA.classList.remove('is-loading');
        this.primaryCTA.classList.add('is-success');
        this.syncStickyATB();
        const parsedState = await this.ensureCartSections(response);
        this.openCartDrawer(parsedState);
        return;
      } else if (purchaseType === 'subscription_plus_gift' || purchaseType === 'one_time_plus_gift') {
        const bundleVariantId = this.primaryCTA.dataset.selectedBundleVariantId || this.selectedBundleVariantId;
        const sellingPlanId = purchaseType === 'subscription_plus_gift' ? (this.primaryCTA.dataset.selectedSellingPlanId || this.selectedSellingPlanId || '').trim() : '';
        if (!bundleVariantId) throw new Error('Main product and gift product are required for this option.');
        await this.cartAdd(variantId, 1, sellingPlanId || '');
        response = await this.cartAdd(bundleVariantId, 1, '');
        if ((this.selectedPurchaseType === 'subscription_plus_gift' || this.selectedPurchaseType === 'one_time_plus_gift') && window.celfLinkedTracker) window.celfLinkedTracker.push(variantId);
        this.primaryCTA.classList.remove('is-loading');
        this.primaryCTA.classList.add('is-success');
        this.syncStickyATB();
        const parsedState = await this.ensureCartSections(response);
        this.openCartDrawer(parsedState);
        return;
      } else if (purchaseType === 'subscription') {
        const sellingPlanId = this.primaryCTA.dataset.selectedSellingPlanId || this.selectedSellingPlanId;
        if (!sellingPlanId) throw new Error('Selling plan ID is required for subscriptions.');
        response = await this.cartAdd(variantId, 1, sellingPlanId);
      } else {
        response = await this.cartAdd(variantId, 1, '');
      }

      // Success — show tick, then open cart drawer
      this.primaryCTA.classList.remove('is-loading');
      this.primaryCTA.classList.add('is-success');
      this.syncStickyATB();

      const parsedState = await this.ensureCartSections(response);
      this.openCartDrawer(parsedState);

    } catch (error) {
      console.error('[HomeHeroProductRedesign] addToCart error:', error);
      this.showErrorMessage(error.message || 'Error adding product to basket. Please try again.');
      this.primaryCTA.classList.remove('is-loading');
      this.primaryCTA.classList.add('is-error');
      this.syncStickyATB();
    } finally {
      // Reset button back to its normal state after a short delay
      setTimeout(() => {
        this.primaryCTA.disabled = false;
        this.primaryCTA.classList.remove('is-loading', 'is-success', 'is-error');
        const selected = this.container?.querySelector('.purchase-option__button.primary');
        if (selected) this.updatePrimaryCTA(selected);
      }, 2500);
    }
  }

  // ---------------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------------

  showSuccessMessage(message = 'Product added to basket!') {
    this._showNotification(message, '#2e7d32', '\u2713');
  }

  showErrorMessage(message = 'Error adding product to basket. Please try again.') {
    this._showNotification(message, '#c62828', '\u2717');
  }

  _showNotification(message, bgColor, icon) {
    const el = document.createElement('div');
    el.setAttribute('style',
      `position:fixed;top:20px;right:20px;z-index:99999;padding:14px 22px;
       border-radius:8px;color:#fff;font-size:15px;font-family:sans-serif;
       background:${bgColor};box-shadow:0 4px 12px rgba(0,0,0,.25);
       display:flex;align-items:center;gap:8px;opacity:0;
       transition:opacity .3s ease;`
    );
    el.innerHTML = `<span style="font-size:18px">${icon}</span><span>${message}</span>`;
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 400);
    }, 4000);
  }

  updateStickyOffset() {
    HomeHeroProductRedesign.updateAllStickyOffsets();
  }

  static updateAllStickyOffsets() {
    const gapPx = 12;
    let bottomY = 0;

    const consider = (el) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.height <= 0) return;
      if (r.bottom <= 0) return;
      if (r.top > window.innerHeight) return;
      bottomY = Math.max(bottomY, r.bottom);
    };

    consider(document.querySelector('#shopify-section-announcement-bar'));
    consider(document.getElementById('shopify-section-header'));

    if (bottomY === 0) {
      const fallback =
        document.querySelector('#shopify-section-header .header-wrapper') ||
        document.querySelector('header.header') ||
        document.querySelector('header');
      consider(fallback);
    }

    const px = Math.round(bottomY + gapPx);
    document.documentElement.style.setProperty('--sticky-offset', `${px}px`);
  }

  resetFixedGallery() {
    const wrap = this.stickyWrap;
    if (!wrap) return;
    wrap.dataset.heroStickMode = '';
    wrap.classList.remove('hero-gallery--js-fixed');
    wrap.style.cssText = '';
  }

  /**
   * Desktop: keep the gallery visible under the header via position:fixed while this hero is on screen.
   * CSS sticky often fails on display:flex galleries or when ancestors clip overflow.
   */
  syncFixedGallery() {
    const wrap = this.stickyWrap;
    const col = this.mediaCol;
    if (!wrap || !col) return;

    if (!this.desktopStickMq.matches) {
      this.resetFixedGallery();
      return;
    }

    const raw = getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset').trim();
    const topPx = parseFloat(raw) || 88;
    const sr = this.container.getBoundingClientRect();
    const wrapH = wrap.offsetHeight;
    const colRect = col.getBoundingClientRect();
    const pad = 8;

    if (sr.top > topPx) {
      this.resetFixedGallery();
      return;
    }

    if (sr.bottom < topPx + wrapH + pad) {
      wrap.classList.remove('hero-gallery--js-fixed');
      wrap.dataset.heroStickMode = 'bottom';
      wrap.style.cssText = '';
      Object.assign(wrap.style, {
        position: 'absolute',
        top: 'auto',
        bottom: '0',
        left: '0',
        right: '0',
        width: '100%',
        zIndex: '2'
      });
      return;
    }

    wrap.dataset.heroStickMode = 'fixed';
    wrap.classList.add('hero-gallery--js-fixed');
    wrap.style.cssText = '';
    Object.assign(wrap.style, {
      position: 'fixed',
      top: `${topPx}px`,
      left: `${Math.round(colRect.left)}px`,
      width: `${Math.round(colRect.width)}px`,
      bottom: 'auto',
      right: 'auto',
      zIndex: '2'
    });
  }

  // Public method to programmatically select a purchase option
  selectPurchaseOption(index) {
    if (this.purchaseOptions[index]) {
      this.purchaseOptions[index].click();
    }
  }

  // Public method to go to a specific image
  goToImageByIndex(index) {
    this.goToImage(index);
  }

  // Cleanup method
  destroy() {
    // Touch listeners on mainView are not removed (section teardown is rare)
  }
}

/**
 * Page switcher (Skin ↔ Sensitive): prefetch only.
 * Cross-document View Transitions are handled by the browser (Chrome 126+)
 * on same-origin link clicks — not via startViewTransition + location.assign.
 * Opt-in: view-transitions.css when “Show page switcher” is on for this section.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/View_Transition_API/Using
 * Preventing default on the outgoing link would block the browser transition.
 */
function initHeroPageSwitcherPrefetch() {
  document.querySelectorAll('a.hero-product-redesign__page-switch-btn[href]').forEach((anchor) => {
    const href = anchor.getAttribute('href');
    if (!href || href === '#') return;

    anchor.addEventListener(
      'pointerenter',
      () => {
        if (anchor.dataset.prefetched) return;
        try {
          const url = new URL(href, window.location.href);
          if (url.origin !== window.location.origin) return;
          anchor.dataset.prefetched = '1';
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = url.href;
          document.head.appendChild(link);
        } catch (_) {
          /* ignore */
        }
      },
      { passive: true }
    );

    anchor.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (anchor.getAttribute('aria-current') === 'page') {
        e.preventDefault();
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.home-hero-product-redesign').forEach((root) => {
    new HomeHeroProductRedesign(root);
  });
  initHeroPageSwitcherPrefetch();
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = HomeHeroProductRedesign;
}
